import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { RedirectToStatusOrShowOfferStepWrapper } from '@components/TireOffer/RedirectToStatusOrShowOfferStepWrapper';
import { getTireOfferTotal } from '@components/TireOffer/tireOfferTotal';
import { TireOptionCard } from '@components/TireOffer/TireSelection/TireOptionCard';
import { t } from '@lingui/core/macro';
import { TireClassification, TireQuoteItemRecordCustomer, TireQuoteRecordCustomer, TireSeason } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { FixedBottomBarContainer, NoddiButton } from 'noddi-ui-web';
import { customerRoutes, formatCurrencyAmount } from 'noddi-util';
import { ErrorPage } from 'noddi-web';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { useTireOfferStore } from '../../../stores/tireOfferStore';

export const TireSelection = () => {
  const { slug } = useParams() as { slug: string };
  const { brandEmail } = useCurrentBrand();

  return (
    <RedirectToStatusOrShowOfferStepWrapper>
      {(offer) => {
        if (offer.quoteItems.length === 0) {
          const carName = offer.carDisplayName;
          return (
            <ErrorPage
              title={t`No tire options available`}
              description={t`We were unable to find any tire options for your ${carName}. Please contact us at ${brandEmail} for assistance.`}
              logInput={{
                value: `Tire offer selection page rendered with empty quoteItems for slug: ${slug}, car: ${carName}`,
                severity: 'error'
              }}
            />
          );
        }
        return <TireOptions offer={offer} slug={slug} />;
      }}
    </RedirectToStatusOrShowOfferStepWrapper>
  );
};

const TireOptions = ({ offer, slug }: { offer: TireQuoteRecordCustomer; slug: string }) => {
  const navigate = useNavigate();
  const { selectedQuoteItem, setSelectedQuoteItem } = useTireOfferStore();
  // Coming back from a later step keeps the pick and its quantities; the store outlives one offer, so match the offer.
  const [picked, setPicked] = useState<TireQuoteItemRecordCustomer | null>(() =>
    selectedQuoteItem && offer.quoteItems.some((item) => item.id === selectedQuoteItem.id) ? selectedQuoteItem : null
  );

  // Tire hotel customers have the tires fitted on their stored wheel set, so only mobile customers are promised a visit.
  const isTireHotel = offer.hasActiveTireHotelContractForWheelSet || offer.hasWheelSetInStorageFacility;
  const perks = [
    {
      title: t`Free delivery`,
      description: t`You only pay for the tires and mounting. The price you see is the total.`
    },
    ...(isTireHotel
      ? []
      : [
          {
            title: t`We come to your home`,
            description: t`The tire change is done where your car is parked, at home or at work.`
          }
        ])
  ];

  const goToNextStep = () => {
    if (!picked) {
      return;
    }
    setSelectedQuoteItem(picked);
    // The summary logs the customer in and asks for an address only when none is known.
    navigate(customerRoutes.tireOfferSummary.getPath({ slug }));
  };

  const pickedTotal = picked
    ? formatCurrencyAmount({
        money: { amount: getTireOfferTotal(picked.quoteItemTires, offer.genericItems) },
        decimals: 0
      })
    : null;

  return (
    <ContentWrapperCustomerApp title={getChooseSeasonTitle(offer.season.value)} width='xl' hideBackButton>
      <div className='flex flex-col gap-5'>
        <ul className='grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-10'>
          {perks.map((perk) => (
            <li key={perk.title} className='flex items-start gap-3'>
              <span className='mt-0.5 flex size-5 flex-none items-center justify-center rounded-full bg-success text-white'>
                <NoddiIcon name='Check' size='xSmall' />
              </span>
              <span className='flex flex-col'>
                <span className='font-semibold text-foreground'>{perk.title}</span>
                <span className='text-sm text-muted-foreground'>{perk.description}</span>
              </span>
            </li>
          ))}
        </ul>

        <div
          role='radiogroup'
          aria-label={t`Tire options`}
          className='grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3'
        >
          {sortQuoteItems(offer.quoteItems).map((quoteItem, index) => (
            <TireOptionCard
              key={quoteItem.id}
              // The stored pick carries the quantities chosen before, so its card starts from them.
              quoteItem={picked?.id === quoteItem.id ? picked : quoteItem}
              position={index + 1}
              selected={picked?.id === quoteItem.id}
              onPick={setPicked}
              genericItems={offer.genericItems}
              isTireHotel={isTireHotel}
              slug={slug}
            />
          ))}
        </div>
      </div>

      <FixedBottomBarContainer contentWidth='xl'>
        <div className='flex w-full items-center gap-2'>
          <FlowBackButton backPath={customerRoutes.tireOfferInfo.getPath({ slug })} />
          {/* Right of the action on phones, beside Back from md. */}
          <NavioContactChatWidget size='small' className='max-md:order-last' />
          <NoddiButton className='flex-1 md:ml-auto md:flex-none' disabled={!picked} onClick={goToNextStep}>
            {pickedTotal ? t`Continue · ${pickedTotal}` : t`Choose an option`}
          </NoddiButton>
        </div>
      </FixedBottomBarContainer>
    </ContentWrapperCustomerApp>
  );
};

const getChooseSeasonTitle = (season: TireSeason) => {
  switch (season) {
    case TireSeason.SUMMER:
      return t`Choose summer tires`;
    case TireSeason.WINTER:
      return t`Choose winter tires`;
    case TireSeason.ALL_SEASON:
      return t`Choose all-season tires`;
  }
};

const getClassificationRank = (quoteItem: TireQuoteItemRecordCustomer) => {
  switch (quoteItem.quoteItemTires[0]?.tireInventory.tire.classification) {
    case TireClassification.PREMIUM:
      return 3;
    case TireClassification.QUALITY:
      return 2;
    case TireClassification.ECONOMY:
    case TireClassification.BUDGET:
      return 1;
    default:
      return 0;
  }
};

const sortQuoteItems = (quoteItems: TireQuoteItemRecordCustomer[]) =>
  [...quoteItems].sort((a, b) => {
    if (a.isRecommended !== b.isRecommended) {
      return a.isRecommended ? -1 : 1;
    }
    return getClassificationRank(b) - getClassificationRank(a);
  });
